const TAGS_STORAGE_KEY = 'asi:catalog-tags';
const TAGS_EVENT = 'asi:catalog-tags-updated';

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readTags() {
  if (!isBrowser()) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(TAGS_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function persistTags(tags) {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(TAGS_STORAGE_KEY, JSON.stringify(tags));

  if (typeof window.CustomEvent === 'function') {
    const event = new CustomEvent(TAGS_EVENT, { detail: { tags } });
    window.dispatchEvent(event);
    return;
  }

  const fallbackEvent = document.createEvent('Event');
  fallbackEvent.initEvent(TAGS_EVENT, false, false);
  fallbackEvent.detail = { tags };
  window.dispatchEvent(fallbackEvent);
}

function generateId(prefix = 'tag') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function getTags() {
  return readTags();
}

function toTitleCase(label) {
  return label
    .split(/\s+/)
    .map((word) => {
      if (!word) {
        return '';
      }
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ')
    .trim();
}

export function createTag(name) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!trimmed) {
    throw new Error('Enter a tag name.');
  }

  const tags = readTags();
  const normalized = trimmed.toLowerCase();

  if (tags.some((tag) => typeof tag.name === 'string' && tag.name.toLowerCase() === normalized)) {
    throw new Error('That tag already exists.');
  }

  const tag = {
    id: generateId(),
    name: toTitleCase(trimmed),
    createdAt: new Date().toISOString(),
  };

  tags.unshift(tag);
  persistTags(tags);
  return tag;
}

export function deleteTag(tagId) {
  if (!tagId) {
    return false;
  }

  const tags = readTags();
  const index = tags.findIndex((tag) => tag.id === tagId);

  if (index === -1) {
    return false;
  }

  tags.splice(index, 1);
  persistTags(tags);
  return true;
}

export function subscribeToTagChanges(callback) {
  if (!isBrowser()) {
    return () => {};
  }

  const handler = (event) => {
    const detail = event && event.detail;
    const tags = (detail && detail.tags) || readTags();
    callback(tags);
  };

  window.addEventListener(TAGS_EVENT, handler);

  return () => {
    window.removeEventListener(TAGS_EVENT, handler);
  };
}
